import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { pushNotification } from './notificationSlice';
import { NotificationType } from '../../components/notifications/notification-component';
import { useDispatch } from 'react-redux';
import { logout } from './authSlice';

export interface Reviewer {
  badgeNumber: string;
  agency: string;
  district: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  orgName: string;
}

export interface LinkOption {
  firstName: string;
  lastName: string;
  badgeNumber: string;
  agency: string;
  district: string;
  email: string;
}

export interface lawEnforcementConfigurationState {
  unassignedReviewers?: Reviewer[];
  linkOptions?: LinkOption[];
  reviewers?: Reviewer[];
  user?: Reviewer;
  isLoading: boolean;
  error: string | null;
}

const initialState: lawEnforcementConfigurationState = {
  reviewers: [],
  linkOptions: [],
  unassignedReviewers: [],
  user: undefined,
  isLoading: false,
  error: null,
};

export const getMyLEUser = createAsyncThunk(
  'lawEnforcementCollisions/getMyLEUser',
  async (
    { csrService }: { csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const myuser = await csrService.get(
        '/api/v2/law-enforcement-officers/me/',
      );

      return myuser.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAllUnassignedLEOfficers = createAsyncThunk(
  'lawEnforcementCollisions/getAllUnassignedLEOfficers',
  async (
    { csrService }: { csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const allReviewers = await csrService.get(
        '/api/v2/law-enforcement-officers?unassigned_users',
      );

      const mappedReviewers: Reviewer[] = [];

      allReviewers.data.map((reviewer: any) => {
        mappedReviewers.push({
          badgeNumber: reviewer.le_badge,
          agency: reviewer.le_agency,
          district: reviewer.le_district,
          firstName: reviewer.first_name,
          lastName: reviewer.last_name,
          name: `${reviewer.first_name} ${reviewer.last_name}`,
          email: reviewer.email,
          orgName: reviewer.org_name,
        });
      });

      return mappedReviewers;
    } catch (error: any) {
      if (error.response && error.response.status) {
        if (error.response.status === 451) {
          localStorage.clear();
          sessionStorage.clear();
          dispatch(logout());
          window.location.replace("/");
        }
        return rejectWithValue([]);
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAllLEOfficers = createAsyncThunk(
  'lawEnforcementCollisions/getAllLEOfficers',
  async (
    { agency, csrService }: { agency?: string, csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      let query = ''
      if(agency) {
        query = `?agency=${agency}`;
      }
      const allReviewers = await csrService.get(
        '/api/v2/law-enforcement-officers' + query,
      );

      const mappedReviewers: Reviewer[] = [];

      allReviewers.data.map((reviewer: any) => {
        mappedReviewers.push({
          badgeNumber: reviewer.le_badge,
          agency: reviewer.le_agency,
          district: reviewer.le_district,
          firstName: reviewer.first_name,
          lastName: reviewer.last_name,
          name: `${reviewer.first_name} ${reviewer.last_name}`,
          email: reviewer.email,
          orgName: reviewer.org_name,
        });
      });

      return mappedReviewers;
    } catch (error: any) {
      if (error.response && error.response.status) {
        if (error.response.status === 451) {
          localStorage.clear();
          sessionStorage.clear();
          dispatch(logout());
          window.location.replace("/");
        }
        return rejectWithValue([]);
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getOfficerECollisionLinkOptions = createAsyncThunk(
  'lawEnforcementCollisions/getOfficerECollisionLinkOptions',
  async (
    {
      reviewer,
      csrService,
    }: {
      reviewer: Reviewer;
      csrService: AxiosInstance;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(
        `/api/v2/law-enforcement-officers?link_options&le_email=${reviewer.email}`,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateLEOfficer = createAsyncThunk(
  'lawEnforcementCollisions/updateLEOfficer',
  async (
    {
      reviewer,
      csrService,
    }: {
      reviewer: Reviewer;
      csrService: AxiosInstance;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.put(
        `/api/v2/law-enforcement-officers/admin`,
        {
          le_badge: reviewer.badgeNumber,
          le_agency: reviewer.agency,
          le_district: reviewer.district,
        },
      );
      const updated_reviewer = {
        badgeNumber: response.data.le_badge,
        agency: response.data.le_agency,
        district: response.data.le_district,
        firstName: response.data.first_name,
        lastName: response.data.last_name,
        name: `${response.data.first_name} ${response.data.last_name}`,
        email: response.data.email,
        orgName: response.data.org_name,
      };
      return updated_reviewer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const linkLEOfficer = createAsyncThunk(
  'lawEnforcementCollisions/linkLEOfficer',
  async (
    {
      reviewer,
      csrService,
    }: {
      reviewer: Reviewer;
      csrService: AxiosInstance;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.post(
        `/api/v2/law-enforcement-officers/admin`,
        {
          badge: reviewer.badgeNumber,
          agency: reviewer.agency,
          district: reviewer.district,
          email: reviewer.email,
        },
      );

      const updated_reviewer = {
        badgeNumber: response.data.le_badge,
        agency: response.data.le_agency,
        district: response.data.le_district,
        firstName: response.data.first_name,
        lastName: response.data.last_name,
        name: `${response.data.first_name} ${response.data.last_name}`,
        email: response.data.email,
        orgName: response.data.org_name,
      };

      return updated_reviewer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const lawEnforcementConfigSlice = createSlice({
  name: 'lawEnforcementConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateLEOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        updateLEOfficer.fulfilled,
        (state, action: PayloadAction<any>) => {
          const updated = action.payload;
          state.reviewers = state.reviewers!.map((reviewer) =>
            reviewer.email === updated.email ? updated : reviewer,
          );

          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        updateLEOfficer.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(getMyLEUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMyLEUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.user = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(getMyLEUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getAllLEOfficers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getAllLEOfficers.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.reviewers = action.payload;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        getAllLEOfficers.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(getAllUnassignedLEOfficers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getAllUnassignedLEOfficers.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.unassignedReviewers = action.payload;
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        getAllUnassignedLEOfficers.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(getOfficerECollisionLinkOptions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getOfficerECollisionLinkOptions.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.linkOptions = [];
          action.payload.map((option: any) => {
            state.linkOptions!.push({
              badgeNumber: option.badgeNumber,
              agency: option.businessAreaCode,
              district: option.districtCode,
              email: option.email,
              firstName: option.firstName,
              lastName: option.lastName,
            });
          });
          state.isLoading = false;
          state.error = null;
        },
      )
      .addCase(
        getOfficerECollisionLinkOptions.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(linkLEOfficer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(linkLEOfficer.fulfilled, (state, action) => {
        const le_user = action.payload;
        if (state.unassignedReviewers) {
          state.unassignedReviewers = state.unassignedReviewers.filter(
            (reviewer) => reviewer.email !== le_user.email,
          );
        }
        if (state.reviewers) {
          state.reviewers.push(le_user);
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(linkLEOfficer.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export default lawEnforcementConfigSlice.reducer;
function dispatch(arg0: any) {
  throw new Error('Function not implemented.');
}

